import type { SmartAttributionAssignment, SmartAttributionParams, SmartAttributionResult } from './types';
import { DEBUG_DEFAULTS } from './types';

interface ClusterState {
  userId: number;
  targetPercentage: number;
  targetSalesAmount: number;
  assignedSalesAmount: number;
  assignments: SmartAttributionAssignment[];
}

const cloneAssignment = (assignment: SmartAttributionAssignment): SmartAttributionAssignment => ({ ...assignment });

const buildClusters = (params: SmartAttributionParams['targets'], totalSales: number): ClusterState[] => params.map((target) => ({
  userId: target.userId,
  targetPercentage: target.percentage,
  targetSalesAmount: totalSales > 0 ? (totalSales * target.percentage) / 100 : 0,
  assignedSalesAmount: 0,
  assignments: [],
}));

const ratio = (cluster: ClusterState, epsilon: number) => {
  const denominator = cluster.targetSalesAmount + epsilon;
  if (denominator <= 0) {
    return 0;
  }
  return cluster.assignedSalesAmount / denominator;
};

const assign = (cluster: ClusterState, assignment: SmartAttributionAssignment) => {
  cluster.assignments.push(cloneAssignment(assignment));
  cluster.assignedSalesAmount += assignment.salesAmount;
};

export function computePerformanceAttribution({ copros, targets, debugParams }: SmartAttributionParams): SmartAttributionResult {
  const effectiveParams = { ...DEBUG_DEFAULTS, ...(debugParams ?? {}) };
  const epsilon = effectiveParams.epsilon;
  const capacityTolerance = effectiveParams.capacityTolerance;

  const totalSalesAmount = copros.reduce((acc, copro) => acc + (copro.salesAmount || 0), 0);
  const clusters = buildClusters(targets, totalSalesAmount);

  const sortedAssignments: SmartAttributionAssignment[] = copros
    .map((copro) => ({
      coproId: copro.id,
      coproName: copro.name,
      salesAmount: copro.salesAmount || 0,
      lat: copro.lat ?? null,
      long: copro.long ?? null,
    }))
    .sort((a, b) => b.salesAmount - a.salesAmount);

  const withinTolerance = (assigned: number, target: number) => {
    if (target <= 0) {
      return true;
    }
    const delta = Math.abs(assigned - target);
    return delta <= (target * capacityTolerance) + epsilon;
  };

  targets.forEach((target, index) => {
    const cluster = clusters[index];
    if (!cluster) {
      return;
    }

    while (sortedAssignments.length > 0 && !withinTolerance(cluster.assignedSalesAmount, cluster.targetSalesAmount)) {
      const next = sortedAssignments.shift();
      if (!next) {
        break;
      }
      assign(cluster, next);
    }
  });

  while (sortedAssignments.length > 0) {
    const next = sortedAssignments.shift();
    if (!next) {
      continue;
    }
    const targetIndex = clusters.reduce((bestIndex, candidate, idx, arr) => {
      if (idx === 0) {
        return 0;
      }
      return ratio(arr[idx], epsilon) < ratio(arr[bestIndex], epsilon) ? idx : bestIndex;
    }, 0);
    const targetCluster = clusters[targetIndex];
    assign(targetCluster, next);
  }

  const clustersWithCoverage = clusters.map((cluster) => ({
    userId: cluster.userId,
    targetPercentage: cluster.targetPercentage,
    targetSalesAmount: cluster.targetSalesAmount,
    assignedSalesAmount: cluster.assignedSalesAmount,
    assignments: cluster.assignments,
    coveragePercentage: totalSalesAmount > 0 ? (cluster.assignedSalesAmount / totalSalesAmount) * 100 : 0,
    anchor: null,
  }));

  return {
    totalSalesAmount,
    clusters: clustersWithCoverage,
  };
}

